import React from 'react'
import { Form } from 'antd'
import { useIntl } from 'react-intl'

import IntlMessages from '../../util/IntlMessages'
import { FilterTrigger, FilterTriggerRow, FilterRowItem, FilterValues, FilterConfig, useFilterRowLabels, buildLabelsFilterRowItem } from '../../design-system/FilterTrigger'
import { IDepartmentOptionSource, toDepartmentFilterOptions } from '@vacationtracker/shared/functions/department-options'
import { IFilter } from '@vacationtracker/shared/types/filter'
import { ILocationFilterSource, locationOptionsByCountry } from '@vacationtracker/shared/functions/location-country-groups'

interface INamedItem {
  id: string
  name: string
}

export interface IAutomationLeaveType extends INamedItem {
  color?: string
}

type FormatMessage = (descriptor: { id: string }, values?: Record<string, string>) => string

export interface IBuildAutomationFilterConfigsParams {
  locations: ILocationFilterSource[]
  teams: IDepartmentOptionSource[]
  labels: INamedItem[]
  labelsEnabled: boolean
  formatMessage: FormatMessage
}

export const EMPTY_AUTOMATION_FILTER: IFilter = { locationIds: [], teamIds: [], labelIds: [] }

export function buildAutomationFilterConfigs(params: IBuildAutomationFilterConfigsParams): FilterRowItem[] {
  const { locations, teams, labels, labelsEnabled, formatMessage } = params
  const clearLabel = (name: string) => formatMessage({ id: 'users.table.clearFilter' }, { filter: name })
  const locationsLabel = formatMessage({ id: 'app.locations' })
  const departmentsLabel = formatMessage({ id: 'app.departments' })
  const labelsLabel = formatMessage({ id: 'app.labels' })

  const locationsByCountry = locationOptionsByCountry(locations, formatMessage({ id: 'locationPlace.card.noCountrySet' }))

  return [
    {
      id: 'locations',
      label: locationsLabel,
      options: locationsByCountry ?? locations.map((location) => ({ value: location.id, label: location.name })),
      keepOrder: Boolean(locationsByCountry),
      clearLabel: clearLabel(locationsLabel),
    },
    { id: 'departments', label: departmentsLabel, options: toDepartmentFilterOptions(teams), clearLabel: clearLabel(departmentsLabel) },
    buildLabelsFilterRowItem({ labels, labelsEnabled, formatMessage, clearLabel: clearLabel(labelsLabel) }),
  ]
}

export function toFilterValues(filter: IFilter): FilterValues {
  return {
    locations: filter.locationIds || [],
    departments: filter.teamIds || [],
    labels: filter.labelIds || [],
  }
}

export function toAutomationFilter(values: FilterValues): IFilter {
  return {
    locationIds: values.locations || [],
    teamIds: values.departments || [],
    labelIds: values.labels || [],
  }
}

export function buildLeaveTypesFilterConfig(leaveTypes: IAutomationLeaveType[], label: string): FilterConfig {
  return {
    id: 'leaveTypes',
    label,
    keepOrder: true,
    options: leaveTypes.map((leaveType) => ({ value: leaveType.id, label: leaveType.name, swatch: leaveType.color })),
  }
}

/** An empty selection applies to every leave type, so the summaries list them all. */
export function selectedLeaveTypeNames(leaveTypes: IAutomationLeaveType[], selectedIds: string[] | undefined): string[] {
  const selected = selectedIds?.length ? leaveTypes.filter((leaveType) => selectedIds.includes(leaveType.id)) : leaveTypes
  return selected.map((leaveType) => leaveType.name)
}

export interface IAutomationFilterRowProps {
  locations: ILocationFilterSource[]
  teams: IDepartmentOptionSource[]
  labels: INamedItem[]
  labelsEnabled: boolean
  value: IFilter
  onChange: (filter: IFilter) => void
}

export const AutomationFilterRow = ({ locations, teams, labels, labelsEnabled, value, onChange }: IAutomationFilterRowProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const rowLabels = useFilterRowLabels()
  const configs = buildAutomationFilterConfigs({ locations, teams, labels, labelsEnabled, formatMessage })
  const values = toFilterValues(value)
  const hasActiveFilters = Boolean(values.locations.length || values.departments.length || values.labels.length)

  return (
    <Form.Item label={<IntlMessages id="components.filterSimple.filterBy" />} tooltip={<IntlMessages id="automationsForm.advancedFilter.tooltipInfo" />}>
      <FilterTriggerRow
        configs={configs}
        values={values}
        onChange={(next) => onChange(toAutomationFilter(next))}
        visibleLimit={configs.length}
        showClearAll={hasActiveFilters}
        onClearAll={() => onChange(EMPTY_AUTOMATION_FILTER)}
        labels={rowLabels}
      />
    </Form.Item>
  )
}

export interface IAutomationLeaveTypesTriggerProps {
  leaveTypes: IAutomationLeaveType[]
  /** Injected by the wrapping Form.Item. */
  value?: string[]
  onChange?: (value: string[]) => void
  disabled?: boolean
}

export const AutomationLeaveTypesTrigger = ({ leaveTypes, value, onChange, disabled }: IAutomationLeaveTypesTriggerProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const rowLabels = useFilterRowLabels()
  const label = formatMessage({ id: 'app.leaveTypes' })

  return (
    <FilterTrigger
      config={buildLeaveTypesFilterConfig(leaveTypes, label)}
      value={value || []}
      onChange={(next) => onChange?.(next)}
      disabled={disabled}
      clearLabel={formatMessage({ id: 'users.table.clearFilter' }, { filter: label })}
      emptyLabel={rowLabels.noOptions}
      searchLabel={rowLabels.search}
      noResultsLabel={rowLabels.noResults}
    />
  )
}

export interface IAutomationLeaveTypesFormItemProps {
  leaveTypes: IAutomationLeaveType[]
  disabled?: boolean
  onChange?: (value: string[]) => void
}

/** The optional `leaveTypeIds` field shared by the automation forms; nothing picked means every leave type. */
export const AutomationLeaveTypesFormItem = ({ leaveTypes, disabled, onChange }: IAutomationLeaveTypesFormItemProps): React.ReactElement => (
  // `required` only hides antd's own "(optional)" mark, which this label already carries; there are no rules.
  <Form.Item name="leaveTypeIds" label={<IntlMessages id="automations.leaveTypesOptional" />} required>
    <AutomationLeaveTypesTrigger leaveTypes={leaveTypes} disabled={disabled} onChange={onChange} />
  </Form.Item>
)
